/**
 * @author Benedikt Schindler, www.owayo.com
 */

/**
 * @file Loader für die Modell Daten
 */

import {THREE} from "./resource/three.js"

import DEBUG from "./DEBUG";
import {gunzip} from "fflate"
class Vector3D {
    x = 0;
    y = 0;
    z = 0;
}

export class OwayoLoaderReturnObject {
    object:any = new THREE.Object3D();
    dimensions:Vector3D = new Vector3D();
}

export type ThreeDModelLoadedEventObject = {
    object;
    dimensions;
    model;
    features;
    cut;
}

class MirlFile {
    mirlDataByteArray:Uint8Array;
    parserIndex = 0;
    FileLength = 0;
    FileVersion = '';

    constructor(mirlDataByteArray:Uint8Array) {
        this.mirlDataByteArray = mirlDataByteArray;
        this.loadFileLength();
        this.loadFileVersion();
    }


    private loadFileLength() {
        this.FileLength = this.mirlDataByteArray.byteLength;
        if (this.FileLength < 1) {
            if (DEBUG) {
                console.error("There was no file loaded! ");
            }
        }
    }

    private loadFileVersion() {
        this.FileVersion = this.readString();

        if (this.FileVersion.indexOf("v1.1") !== 0) {
            if (DEBUG) {
                console.error("This MIRL file version is not supported! - " + this.FileVersion);
            }
        }
    }

    readByte() {

        // Calc result
        const result = this.mirlDataByteArray[this.parserIndex];
        //Set Parser and return
        this.parserIndex = this.parserIndex + 1;
        return result;
    }

    readWord() {
        let result;
        // Calc result
        result = this.mirlDataByteArray[this.parserIndex];
        result = result + this.mirlDataByteArray[this.parserIndex + 1] * 256;

        //Set Parser and return
        this.parserIndex = this.parserIndex + 2;
        return result;
    }

    readInteger() {
        let result;
        // Calc result
        result = this.mirlDataByteArray[this.parserIndex];
        result = result + this.mirlDataByteArray[this.parserIndex + 1] * 256;
        result = result + this.mirlDataByteArray[this.parserIndex + 2] * 65536;
        result = result + this.mirlDataByteArray[this.parserIndex + 3] * 16777216;

        //Set Parser and return
        this.parserIndex = this.parserIndex + 4;
        return result;
    }

    readFloat() {
        let BinStr = '',
            i;

        for (i = 0; i < 4; i += 1) {
            BinStr = BinStr + String.fromCharCode(this.mirlDataByteArray[this.parserIndex + i]);
        }

        const result = MirlFile.binCharsToFloat(BinStr);

        this.parserIndex = this.parserIndex + 4;
        return result;
    }

    readString() {
        const Length = this.readInteger();
        let result = '',i;

        for (i = 0; i < Length; i += 1) {
            result = result + String.fromCharCode(this.mirlDataByteArray[this.parserIndex + i]);
        }

        this.parserIndex = this.parserIndex + Length;
        return result;
    }

    seek(position) {
        this.parserIndex = position;
    }

    EoF():boolean {
        return ( this.parserIndex >= this.FileLength );
    }

    private static binCharsToFloat(fInString) {
        let mantisse;


        if (fInString === null) {
            return;
        }
        fInString = fInString.toString();
        if (fInString.length < 4) {
            return;
        }

        const fBits = this.getBitsFromString(fInString);

        const vorzeichen = parseInt(fBits[0], 10) ? -1 : 1;
        const exponent = parseInt(fBits.substring(1, 9), 2) - 127;
        if (exponent === -127) {
            mantisse = 0;
        } else {
            mantisse = this.getMantisse(fBits);
        }
        return (parseFloat((vorzeichen * Math.pow(2, exponent) * mantisse).toFixed(3)));
    }

    private static getMantisse(fBits) {
        let mantisse = 1, i;
        for (i = 0; i < 23; i += 1) {
            if (parseInt(fBits[9 + i], 10) === 1) {
                mantisse = mantisse + 1 / Math.pow(2, i + 1);
            }
        }
        return mantisse;
    }

    private static padFromLeftWithZerosToLength(curByte, desiredLength) {
        let bit;
        const byteLen = curByte.length;
        for (bit = 0; bit < (desiredLength - byteLen); bit += 1) {
            curByte = '0' + curByte;
        }
        return curByte;
    }

    private static getBitsFromString(fInString) {
        let i, currentByte, fBits = "";
        for (i = 0; i < 4; i += 1) {
            currentByte = (fInString.charCodeAt(i) & 255).toString(2);

            if (currentByte.length < 8) {
                currentByte = this.padFromLeftWithZerosToLength(currentByte, 8);
            }
            fBits = currentByte + fBits;
        }
        return fBits;
    }

}

export class OwayoLoader {
    manager:any;
    mirlFile:MirlFile|null = null;

    result:OwayoLoaderReturnObject = new OwayoLoaderReturnObject();
    totalVertices = 0;
    totalFaces = 0;

    LastLoadedMinX:number = Number.MAX_VALUE;
    LastLoadedMaxX:number = -Number.MAX_VALUE;
    LastLoadedMinY:number = Number.MAX_VALUE;
    LastLoadedMaxY:number = -Number.MAX_VALUE;
    LastLoadedMinZ:number = Number.MAX_VALUE;
    LastLoadedMaxZ:number = -Number.MAX_VALUE;


    constructor(manager) {
        this.manager = ( manager !== undefined ) ? manager : THREE.DefaultLoadingManager;
    }


    loadZipped(url) {
        return new Promise((resolve, reject)=>{
            // https://github.com/nodeca/pako
            const $this = this,
                oReq = new XMLHttpRequest();
            oReq.open("GET", url, true);
            oReq.responseType = "arraybuffer";
            oReq.onload = function () {//oEvent) {
                let arrayBuffer = oReq.response, // Note: not oReq.responseText
                    byteArray;
                if (arrayBuffer) {
                    byteArray = new Uint8Array(arrayBuffer);
                    gunzip(byteArray, {}, (err, unzipped)=>{
                        $this.mirlFile = new MirlFile(unzipped);
                        resolve($this.parse());
                        byteArray = null;
                        arrayBuffer = null;
                        $this.mirlFile = null;
                    })

                }
            };
            oReq.onerror = ()=>reject();
            oReq.send(null);
        })


        // MirlFileZipExtractor.load(url).then((r)=>{
        //     console.dir(r)
        // });
    }

    load(url, callbackOnLoad, onError) {
        const $this = this,
            oReq = new XMLHttpRequest();
        oReq.open("GET", url, true);
        oReq.responseType = "arraybuffer";
        oReq.onload = function () {//oEvent) {
            let arrayBuffer = oReq.response;	// Note: not oReq.responseText
            if (arrayBuffer) {
                $this.mirlFile = new MirlFile(new Uint8Array(arrayBuffer));
                callbackOnLoad($this.parse());
                $this.mirlFile = null;
                arrayBuffer = null;
            }
        };
        oReq.onerror = onError;
        oReq.send(null);

    }

    parse() {

        this.loadModelObjects();
        this.moveObjectGroupOverOrigin();
        this.makeThreeJSUpdateAllGeometries();
        this.setResultDimensions();
        return this.result;

    }

    private moveObjectGroupOverOrigin() {
        let i, l;
        const children = this.result.object.children;

        for (i = 0, l = children.length; i < l; i += 1) {
            OwayoLoader.moveThreeJSObject(children[i].geometry, this.getCenterOfAllObjects());
        }
    }

    private makeThreeJSUpdateAllGeometries() {
        let i, l;
        const children = this.result.object.children;

        for (i = 0, l = children.length; i < l; i += 1) {
            OwayoLoader.makeThreeJSUpdateGeometry(children[i].geometry);
        }
    }

    private setResultDimensions() {
        this.result.dimensions.x = this.LastLoadedMaxX - this.LastLoadedMinX;
        this.result.dimensions.y = this.LastLoadedMaxY - this.LastLoadedMinY;
        this.result.dimensions.z = this.LastLoadedMaxZ - this.LastLoadedMinZ;
    }

    private loadModelObjects() {
        while (!(this.mirlFile as MirlFile).EoF()) {
            this.parseModelObjectFromCurrentIndex();
        }
    }

    private getCenterOfAllObjects():Vector3D {
        const result: Vector3D = new Vector3D();

        result.x = (this.LastLoadedMinX + this.LastLoadedMaxX) / 2;
        result.y = (this.LastLoadedMinY + this.LastLoadedMaxY) / 2;
        result.z = (this.LastLoadedMinZ + this.LastLoadedMaxZ) / 2;

        return result;
    }

    private static moveThreeJSObject(geometry, deltaVector3D) {
        let index, ll, ii;

        // Wir gehen nochmal alle Vertices durch und verschieben sie so
        // dass wir in der Mitte unseres KoordinatenSystems sind.


        for (index = 0, ll = geometry.attributes.position.array.length / 3; index < ll; index += 1) {
            ii = index * 3;
            geometry.attributes.position.array[ii] -= deltaVector3D.x;
            geometry.attributes.position.array[ii + 1] -= deltaVector3D.y;
            geometry.attributes.position.array[ii + 2] -= deltaVector3D.z;
        }

    }

    private static makeThreeJSUpdateGeometry(geometry) {
        geometry.verticesNeedUpdate = true;
        geometry.computeBoundingBox();
        geometry.computeFaceNormals();
        geometry.computeVertexNormals();
        geometry.computeBoundingSphere();
        geometry.buffersNeedUpdate = true;
        geometry.uvsNeedUpdate = true;
    }


    private parseModelObjectFromCurrentIndex() {

        const mirlFileObject = new MirlFileObject(this.mirlFile as MirlFile, this);
        mirlFileObject.parse();
        this.result.object.add(mirlFileObject.getThreeJSObject());
    }

    compareDimensionsToDimensionExtremes(x, y, z) {
        if (x < this.LastLoadedMinX) {
            this.LastLoadedMinX = x;
        }
        if (y < this.LastLoadedMinY) {
            this.LastLoadedMinY = y;
        }
        if (z < this.LastLoadedMinZ) {
            this.LastLoadedMinZ = z;
        }
        if (x > this.LastLoadedMaxX) {
            this.LastLoadedMaxX = x;
        }
        if (y > this.LastLoadedMaxY) {
            this.LastLoadedMaxY = y;
        }
        if (z > this.LastLoadedMaxZ) {
            this.LastLoadedMaxZ = z;
        }
    }

}


class MirlFileObject {

    mirlFile:MirlFile;
    parent:OwayoLoader;

    objectname;
    positions;
    uvs;
    indizes;
    TempVertices;
    anzVertices;
    anzFaces;
    geometry;


    factorUmrechnungWordToFloatX;
    factorUmrechnungWordToFloatY;
    factorUmrechnungWordToFloatZ;

    constructor(mirlFile:MirlFile, parent:OwayoLoader) {
        this.mirlFile = mirlFile;
        this.parent = parent;
        this.geometry = new THREE.BufferGeometry();

    }

    getThreeJSObject() {
        const mesh = new THREE.Mesh(this.geometry, new THREE.MeshLambertMaterial({
            transparent: false,
            side: THREE.DoubleSide
        }));
        mesh.name = this.objectname;

        return mesh;
    }


    parse() {
        this.readObjectHeader();
        this.createObjectStructure();

        if (DEBUG) {
            this.logHeaderDataToConsole();
        }

        this.readVertices();
        this.readFaces();
        this.readUvs();
    }

    private logHeaderDataToConsole() {
        this.parent.totalVertices += this.anzVertices;
        this.parent.totalFaces += this.anzFaces;
        // if(DEBUG) {
        //     console.log(this.objectname + ' - Vert: ' + this.anzVertices + ' / Anz Faces:' + this.anzFaces);
        // }
    }

    private createObjectStructure() {
        // Wenn wir weniger als 65536 vertices haben, so können wir eine
        // Indexed Buffer-Geomertry aufmachen.

        if (this.anzVertices < 65536) {
            this.indizes = new Uint16Array(this.anzFaces * 3);
            //geometry.addAttribute('index', new THREE.BufferAttribute( indizes, 3 ) );
            // there is still a bug with addIndex;
            this.geometry.addIndex(new THREE.BufferAttribute(this.indizes, 1));

            this.positions = new Float32Array(this.anzVertices * 3);
            this.geometry.addAttribute('position', new THREE.BufferAttribute(this.positions, 3));

            this.uvs = new Float32Array(this.anzVertices * 2);
            this.geometry.addAttribute('uv', new THREE.BufferAttribute(this.uvs, 2));
        } else {

            this.positions = new Float32Array(this.anzFaces * 3 * 3);
            this.geometry.addAttribute('position', new THREE.BufferAttribute(this.positions, 3));

            this.uvs = new Float32Array(this.anzFaces * 3 * 2);
            this.geometry.addAttribute('uv', new THREE.BufferAttribute(this.uvs, 2));

            this.TempVertices = new Float32Array(this.anzVertices * 3);

        }


    }

    private readObjectHeader() {
        this.objectname = this.mirlFile.readString();
        this.anzVertices = this.mirlFile.readInteger();
        this.anzFaces = this.mirlFile.readInteger();
        this.factorUmrechnungWordToFloatX = this.mirlFile.readFloat();
        this.factorUmrechnungWordToFloatY = this.mirlFile.readFloat();
        this.factorUmrechnungWordToFloatZ = this.mirlFile.readFloat();
    }

    private readUvs() {
        let i, ii;
        for (i = 0; i < this.anzFaces; i += 1) {

            //  Für jede Ecke des Faces gibt es eine UV-Koordinate


            if (this.geometry.index) {

                // Die indexed BufferGeometry hat nur eine UV-Koordinate pro
                // Vertex! Wir schreiben deshalb hier die UV-Koordinaten auf das
                // entsprechende Vertex.
                // Eine Index-BufferGeometry macht deshalb nur mit einfachen UV-Maps
                // sinn.
                if (this.mirlFile.FileVersion.endsWith('_UVsAsFloat')) {
                    this.uvs[ this.indizes[i * 3]     * 2    ]   = this.mirlFile.readFloat();
                    this.uvs[ this.indizes[i * 3]     * 2 + 1]   = this.mirlFile.readFloat();
                    this.uvs[ this.indizes[i * 3 + 1] * 2]       = this.mirlFile.readFloat();
                    this.uvs[ this.indizes[i * 3 + 1] * 2 + 1]   = this.mirlFile.readFloat();
                    this.uvs[ this.indizes[i * 3 + 2] * 2]       = this.mirlFile.readFloat();
                    this.uvs[ this.indizes[i * 3 + 2] * 2 + 1]   = this.mirlFile.readFloat();
                } else {
                    this.uvs[ this.indizes[i * 3]     * 2]      = this.mirlFile.readWord() / 65535;
                    this.uvs[ this.indizes[i * 3]     * 2 + 1]  = this.mirlFile.readWord() / 65535;
                    this.uvs[ this.indizes[i * 3 + 1] * 2]      = this.mirlFile.readWord() / 65535;
                    this.uvs[ this.indizes[i * 3 + 1] * 2 + 1]  = this.mirlFile.readWord() / 65535;
                    this.uvs[ this.indizes[i * 3 + 2] * 2]      = this.mirlFile.readWord() / 65535;
                    this.uvs[ this.indizes[i * 3 + 2] * 2 + 1]  = this.mirlFile.readWord() / 65535;
                }

            }
            else {
                // Wie haben auf BufferGeometry umgestellt und schreiben nun deshalb alle UV-Koordinaten in einen langen Array
                ii = i * 3 * 2;
                if (this.mirlFile.FileVersion.endsWith('_UVsAsFloat')) {
                    this.uvs[ii] = this.mirlFile.readFloat();
                    this.uvs[ii + 1] = this.mirlFile.readFloat();
                    this.uvs[ii + 2] = this.mirlFile.readFloat();
                    this.uvs[ii + 3] = this.mirlFile.readFloat();
                    this.uvs[ii + 4] = this.mirlFile.readFloat();
                    this.uvs[ii + 5] = this.mirlFile.readFloat();
                } else {
                    this.uvs[ii] = this.mirlFile.readWord() / 65535;
                    this.uvs[ii + 1] = this.mirlFile.readWord() / 65535;
                    this.uvs[ii + 2] = this.mirlFile.readWord() / 65535;
                    this.uvs[ii + 3] = this.mirlFile.readWord() / 65535;
                    this.uvs[ii + 4] = this.mirlFile.readWord() / 65535;
                    this.uvs[ii + 5] = this.mirlFile.readWord() / 65535;
                }
            }


        }
    }

    private readFaces() {

        let i, ii, a, b, c;
        for (i = 0; i < this.anzFaces; i += 1) {

            // Je nachdem wir groß unser Model ist, müssen wir unterschiedliche
            // IntegerTypen parsen.
            if (this.anzVertices < 256) {
                a = this.mirlFile.readByte();
                b = this.mirlFile.readByte();
                c = this.mirlFile.readByte();
            } else {
                if (this.anzVertices < 65536) {
                    a = this.mirlFile.readWord();
                    b = this.mirlFile.readWord();
                    c = this.mirlFile.readWord();
                } else {
                    a = this.mirlFile.readInteger();
                    b = this.mirlFile.readInteger();
                    c = this.mirlFile.readInteger();
                }
            }


            // Wenn wir eine indexed-BufferGeometry haben so müssen wir einfach nur
            // den index übernehmen. Ansonsten müssen wir die Position des Indexes eintragen
            if (this.geometry.index) {
                this.indizes[i * 3] = a;
                this.indizes[i * 3 + 1] = b;
                this.indizes[i * 3 + 2] = c;
            }
            else {

                // Um den richtigen Index im TempVertices Array zu bekommen müssen wir den Index mal 3 nehmen.
                a = a * 3;
                b = b * 3;
                c = c * 3;

                ii = i * 3 * 3;

                this.positions[ii] = this.TempVertices[a];
                this.positions[ii + 1] = this.TempVertices[a + 1];
                this.positions[ii + 2] = this.TempVertices[a + 2];

                this.positions[ii + 3] = this.TempVertices[b];
                this.positions[ii + 4] = this.TempVertices[b + 1];
                this.positions[ii + 5] = this.TempVertices[b + 2];

                this.positions[ii + 6] = this.TempVertices[c];
                this.positions[ii + 7] = this.TempVertices[c + 1];
                this.positions[ii + 8] = this.TempVertices[c + 2];

            }
        }
    }

    private readVertices() {

        let i, ii, x, y, z;
        for (i = 0; i < this.anzVertices; i += 1) {
            x = this.mirlFile.readWord() / this.factorUmrechnungWordToFloatX;
            z = -this.mirlFile.readWord() / this.factorUmrechnungWordToFloatY;    // Wir lesen zu erst z und dann y ein um die Achsen zu vertauschen.
            y = this.mirlFile.readWord() / this.factorUmrechnungWordToFloatZ;    // wir schauen sonst von oben auf das Trikot.
            // In ThreeJS ist die Z Achse die Achse die aus dem Bildschirm geht


            ii = i * 3;

            if (this.geometry.index) {
                this.positions[ii] = x;
                this.positions[ii + 1] = y;
                this.positions[ii + 2] = z;

            }
            else {
                this.TempVertices[ii] = x;
                this.TempVertices[ii + 1] = y;
                this.TempVertices[ii + 2] = z;
            }
            this.parent.compareDimensionsToDimensionExtremes(x, y, z);
        }
    }
}
